import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import OptionsMenuDropdown from "app/pages/sales-intelligence/common-components/options-menu/OptionsMenu/OptionsMenuDropdown";
import { useIsContactBeingRevealed } from "app/pages/sales-intelligence/sub-modules/common/hooks/useContactRevealStatus";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import { selectIsExportFlowActive } from "../../../store/selectors";
import type { ContactSearchEntry, CRMEntity } from "../../../types/common";
import ContactsExportCommonItems from "../../ContactsExportDropdown/ContactsExportCommonItems";

import AiMeetingAgentDropdownItem from "./AiMeetingAgentDropdownItem";
import CraftEmailIconButton from "./CraftEmailIconButton";
import { StyledBaseCell, StyledDDItemWrapper } from "./styles";
import WhatsappLinkButton from "./WhatsappLinkButton";

const ACTIONS_DROPDOWN_WIDTH = 240;

export interface IEngageActionsCellBaseProps {
    onExportItemToExel: (contact: ContactSearchEntry) => void;
    onExportItemToCRM: (contact: ContactSearchEntry, destination: Integration, as?: CRMEntity) => void;
}

interface IProps extends IEngageActionsCellBaseProps {
    rowData: ContactSearchEntry;
    actionButtonDataAutomation?: string;
    viewType?: "list" | "leadGen";
}

const EngageActionsCell = ({
    rowData,
    actionButtonDataAutomation,
    onExportItemToExel,
    onExportItemToCRM,
    viewType = "leadGen",
}: IProps) => {
    const isContactBeingRevealed = useIsContactBeingRevealed(rowData.id);
    const isExportOngoing = useSelector(selectIsExportFlowActive);

    const hasDataToExport = rowData.hasValidEmails || rowData.hasDirectPhoneNumbers || rowData.hasMobilePhoneNumbers;
    const isExportDisabled = !hasDataToExport || isContactBeingRevealed || isExportOngoing;

    const disabledExportTooltip = isContactBeingRevealed
        ? translate("si.contacts_search.action.export.not-revealed-tooltip")
        : "";

    const handleCRMExport = (destination: Integration, as: CRMEntity = "contact") => {
        trackingService.trackExportContactClick();
        onExportItemToCRM(rowData, destination, as);
    };

    const handleExcelExport = () => {
        trackingService.trackExportContactClick();
        onExportItemToExel(rowData);
    };

    const getView = () => {
        const WatchAppElement = (
            <WhatsappLinkButton
                rowData={rowData}
                onClick={() => trackingService.trackSendWhatsappClick()}
            />
        );

        const ContactsElement = (
            <ContactsExportCommonItems
                isExportDisabled={isExportDisabled}
                isUseLockedIcon={false}
                disabledExportTooltip={disabledExportTooltip}
                onExcelExport={handleExcelExport}
                onCRMExport={handleCRMExport}
                withExcelExport={false}
            />
        );

        if (viewType === "list") {
            return (
                <>
                    {WatchAppElement}
                    {ContactsElement}
                </>
            );
        }
        return (
            <>
                {ContactsElement}
                {WatchAppElement}
            </>
        );
    };

    return (
        <StyledBaseCell dataAutomation="contacts-table.cell.actions">
            <CraftEmailIconButton
                domain={rowData.site}
                recipient={rowData.name}
                onClick={() => trackingService.trackCraftEmailClick()}
            />

            <OptionsMenuDropdown
                id={`contact-actions-cell-${rowData.id}`}
                placement="bottom-left"
                buttonIconSize="xs"
                dataAutomation={actionButtonDataAutomation}
                tooltipContent={translate("si.common.more")}
                dropdownWidth={ACTIONS_DROPDOWN_WIDTH}
            >
                <StyledDDItemWrapper>
                    <AiMeetingAgentDropdownItem
                        domain={rowData.site}
                        attendee={rowData.name}
                        onClick={() => trackingService.trackPrepareForMeetingClick()}
                    />

                    {getView()}
                </StyledDDItemWrapper>
            </OptionsMenuDropdown>
        </StyledBaseCell>
    );
};

export default EngageActionsCell;
